import Papa from 'papaparse';
import { SLBPosition, AvailabilityReportItem } from '@/types';
import { recordExportEvent } from '@/services/audit/audit-service';

type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
};

const generateFilename = (stateName: string): string => {
  if (!stateName) {
    const now = new Date();
    const date = now.toISOString().split('T')[0];
    const time = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
    return `inventory-${date}-${time}-unknown.csv`;
  }

  const dateMatch = stateName.match(/(\d{4})-(\d{2})-(\d{2})/);
  const timeMatch = stateName.match(/(\d{1,2}):(\d{2})/);
  const typeMatch = stateName.match(/\(([^)]+)\)/);

  const date = dateMatch ? `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}` : new Date().toISOString().split('T')[0];
  const time = timeMatch ? `${String(timeMatch[1]).padStart(2, '0')}-${timeMatch[2]}` : '00-00';
  const type = typeMatch ? typeMatch[1].toLowerCase() : 'unknown';

  return `inventory-${date}-${time}-${type}.csv`;
};

const L2CP_INNER_IDS = ['l2cpQuantity', 'l2cpAmountEur', 'l2cpRate', 'l2cpGrossRate', 'l2cpSnpDelta'];

export const exportToCSV = (data: SLBPosition[], visibleColumns: ColumnConfig[], stateName: string = ''): void => {
  if (data.length === 0) {
    alert('No data available to export. Please adjust your filters or wait for data to load.');
    return;
  }

  const filename = generateFilename(stateName);

  // When the L2CP group column is visible, include all inner L2CP columns
  const l2cpGroupVisible = visibleColumns.some((col) => col.id === 'lentToCounterparties' && col.visible);

  const visibleColumnIds = visibleColumns
    .filter((col) => {
      if (!col.visible && !(l2cpGroupVisible && L2CP_INNER_IDS.includes(col.id))) return false;
      if (col.id === 'rowNumber') return false;
      return true;
    })
    .map((col) => col.id);

  const columnLabels = visibleColumns
    .filter((col) => visibleColumnIds.includes(col.id))
    .reduce((acc, col) => {
      // Prefix L2CP inner columns to avoid label clashes (e.g. "Quantity" vs "L2CP Quantity")
      acc[col.id] = L2CP_INNER_IDS.includes(col.id) ? `L2CP ${col.label}` : col.label;
      return acc;
    }, {} as Record<string, string>);

  const csvData = data.map((row) => {
    const csvRow: Record<string, unknown> = {};
    visibleColumnIds.forEach((colId) => {
      if (colId === 'lentToCounterparties') {
        const isNostro = /\bnostro\b/i.test(row.accountId ?? '');
        const hasCounterparty = row.counterparty != null && String(row.counterparty).trim() !== '';
        if (isNostro || !hasCounterparty) {
          csvRow[columnLabels[colId]] = '-';
        } else if (row.loans && row.loans.length > 1) {
          const byAccount = new Map<string, number>();
          for (const loan of row.loans) {
            byAccount.set(loan.accountName, (byAccount.get(loan.accountName) ?? 0) + loan.quantityLent);
          }
          csvRow[columnLabels[colId]] = [...byAccount.entries()]
            .map(([name, qty]) => `${name}: ${qty}`)
            .join('; ');
        } else {
          const qty = row.l2cpQuantity ?? row.loans?.[0]?.quantityLent ?? 0;
          csvRow[columnLabels[colId]] = `${row.counterparty}: ${qty}`;
        }
        return;
      }
      const value = row[colId as keyof SLBPosition];
      csvRow[columnLabels[colId]] = value ?? '';
    });
    return csvRow;
  });

  const csv = Papa.unparse(csvData);

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);

  recordExportEvent('inventory.export_csv', { scope: stateName, row_count: data.length });
};

const generateAvailabilityFilename = (stateName: string): string => {
  if (!stateName) {
    const now = new Date();
    const date = now.toISOString().split('T')[0];
    const time = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
    return `availability-report-${date}-${time}.csv`;
  }
  const dateMatch = stateName.match(/(\d{4})-(\d{2})-(\d{2})/);
  const timeMatch = stateName.match(/(\d{1,2}):(\d{2})/);
  const typeMatch = stateName.match(/\(([^)]+)\)/);
  const date = dateMatch ? `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}` : new Date().toISOString().split('T')[0];
  const time = timeMatch ? `${String(timeMatch[1]).padStart(2, '0')}-${timeMatch[2]}` : '00-00';
  const type = typeMatch ? typeMatch[1].toLowerCase() : 'unknown';
  return `availability-report-${date}-${time}-${type}.csv`;
};

export const exportAvailabilityToCSV = (data: AvailabilityReportItem[], stateName: string = '', excludeInternalColumns: boolean = true): void => {
  if (data.length === 0) {
    alert('No data available to export. Please adjust your filters or wait for data to load.');
    return;
  }
  const filename = generateAvailabilityFilename(stateName);
  const csvData = data.map((row) => {
    if (excludeInternalColumns) {
      // Only first 4 columns: Symbol ID, SEDOL, ISIN, Quantity
      return {
        'Symbol ID': row.symbol_id,
        'SEDOL': row.sedol,
        'ISIN': row.isin,
        'Quantity': row.quantity,
      };
    } else {
      // All columns
      const counterpartiesStr = row.counterparties
        .map(cp => `${cp.name}: ${cp.quantity} (${cp.percentage.toFixed(2)}%)`)
        .join('; ');
      // Add "Out" label to transfers if it's an out transfer
      const transfersValue = row.pending_transfers > 0 
        ? `Out: ${row.transfers}` 
        : row.transfers;
      return {
        'Symbol ID': row.symbol_id,
        'SEDOL': row.sedol,
        'ISIN': row.isin,
        'Quantity': row.quantity,
        'Benchmark Fee': row.benchmark_fee,
        'CA': row.ca,
        'Transfers': transfersValue,
        'Counterparties': counterpartiesStr,
      };
    }
  });
  const csv = Papa.unparse(csvData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  recordExportEvent('availability.export_csv', { scope: stateName, row_count: data.length });
};

